import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Breadcrumbs, Button, Divider, Typography, Link } from '@mui/material';
import { ReactNode } from 'react';
import '../tab-section.css';

interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
  active?: boolean;
}

interface PageHeaderProps {
  breadcrumbs?: BreadcrumbItem[];
  actions?: ReactNode;
  showBackButton?: boolean;
  onBack?: () => void;
  backButtonLabel?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  breadcrumbs = [],
  actions,
  showBackButton = false,
  onBack,
  backButtonLabel = 'Back to Library',
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}>
      {/* Left side: Back Button and Breadcrumbs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Back Button (optional) */}
        {showBackButton && onBack && (
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                color: '#0891b2',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                padding: '6px 12px',
                minHeight: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(8, 145, 178, 0.08)',
                },
              }}>
              {backButtonLabel}
            </Button>
            <Divider
              orientation='vertical'
              sx={{
                height: '24px',
                borderColor: '#cbd5e1',
                mx: 0.5,
              }}
            />
          </>
        )}

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <Breadcrumbs separator={<NavigateNextIcon fontSize='small' />}>
            {breadcrumbs.map((crumb, index) => (
              crumb.onClick ? (
                <Link
                  key={index}
                  component='button'
                  variant='body2'
                  onClick={crumb.onClick}
                  sx={{
                    textDecoration: 'none',
                    color: '#0891b2',
                    fontWeight: 500,
                    fontSize: '14px',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: '#0e7490',
                    },
                    cursor: 'pointer',
                  }}>
                  {crumb.label}
                </Link>
              ) : (
                <Typography
                  key={index}
                  variant='body2'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 500,
                    fontSize: '14px',
                  }}
                  color={crumb.active ? 'text.primary' : '#64748b'}>
                  {crumb.label}
                </Typography>
              )
            ))}
          </Breadcrumbs>
        )}
      </Box>

      {/* Right side: Actions */}
      {actions && <Box>{actions}</Box>}
    </Box>
  );
};
